import { Address } from '@/api/types/public';
import logger from '@/utils/client-logger';
import { Country } from './../api/types/public';

/**
 * 根据不同国家的手机号规则去匹配展示手机号
 * @param {*} number 手机号
 * @param {*} format 需要匹配的手机格式
 * 例如美国：'(###) ###-####'
 * @returns
 */
export function formatPhoneNoByRule(number: string, format: string) {
  try {
    // 过滤掉所有非数字字符
    const _number = number.replace(/\D/g, '');

    // 如果输入为空字符串，返回空字符串
    if (!_number) {
      return '';
    }

    let formattedNumber = '';
    let numIndex = 0; // 用于追踪输入数字的位置

    for (let i = 0; i < format.length; i++) {
      if (format[i] === '#') {
        // 如果当前字符是 #，从输入数字中取出相应数字
        if (numIndex < _number.length) {
          formattedNumber += _number[numIndex];
          numIndex++;
        } else {
          // 如果数字不够，停止填充，返回已格式化的部分
          break;
        }
      } else if (numIndex < _number.length) {
        // 如果当前字符不是 #，直接附加到结果字符串中
        formattedNumber += format[i];
      }
    }
    // 返回格式化后的字符串
    return formattedNumber;
  } catch (err) {
    logger.error(err);
    return number;
  }
}

/**
 * 格式化美国邮编
 * @param {*} input
 * @returns
 */
export function formatZipCode(input: string) {
  // 获取当前输入值并去除所有非数字字符
  const value = input.replace(/\D/g, '');
  let formatValue = value;
  // 根据数字长度进行格式化
  if (value.length <= 5) {
    // 如果不超过5位数字，直接显示
    formatValue = value;
  } else if (value.length > 5 && value.length <= 9) {
    // 如果数字长度在6到9之间，格式化为 ZIP+4
    formatValue = `${value.slice(0, 5)}-${value.slice(5)}`;
  } else {
    // 超过9位数字，保持前9位的格式
    formatValue = `${value.slice(0, 5)}-${value.slice(5, 9)}`;
  }

  // 如果需要限制输入长度，可以在这里设置
  if (value.length > 9) {
    formatValue = value; // 保留10个字符，包括连字符
  }
  return formatValue;
}
// 移除emoji
export function removeEmoji(str: string) {
  return str.replace(
    /[^\u0020-\u007E\u00A0-\u00BE\u2E80-\uA4CF\uF900-\uFAFF\uFE30-\uFE4F\uFF00-\uFFEF\u0080-\u009F\u2000-\u201F\r\n]/g,
    '',
  );
}

// 错误码一一对应校验逻辑，谨慎修改！！！！！！！！！
// 错误码到翻译key的映射
export const formErrorKeyMap: Record<number, string> = {
  0: '',
  1: 'cannotShip',
  2: 'firstNameRequired',
  3: 'firstNameLength',
  4: 'nameInvalidChars',
  5: 'lastNameRequired',
  6: 'lastNameLength',
  7: 'nameMaxLength',
  8: 'countryRequired',
  9: 'invalidAddress',
  10: 'addressMaxLength',
  11: 'invalidChars',
  12: 'cityRequired',
  13: 'provinceRequired',
  14: 'postcodeRequired',
  15: 'postcodeInvalid',
  16: 'brazilTaxIdRequired',
  17: 'brazilTaxIdInvalid',
  18: 'regionCodeRequired',
  19: 'phoneRequired',
  20: 'phoneInvalid',
  21: 'phoneFirstDigit',
  22: 'apartmentMaxLength',
  23: 'provinceUnavailable',
  24: 'mexicoTaxIdRequired',
  25: 'mexicoTaxIdInvalid',
};

// 获取错误翻译key的辅助函数
export function getErrorTranslationKey(errorCode: number): string | undefined {
  return formErrorKeyMap[errorCode];
}

export function firstnameValidator(firstname: string, lastname: string): number {
  return (
    addressFormValidatorRules.firstnameValidator(firstname) ||
    addressFormValidatorRules.firstnameAndLastnameLengthValidator(firstname, lastname) ||
    0
  );
}

export function lastnameValidator(lastname: string, firstname: string): number {
  return (
    addressFormValidatorRules.lastnameValidator(lastname) ||
    addressFormValidatorRules.firstnameAndLastnameLengthValidator(firstname, lastname) ||
    0
  );
}

export function countryValidator(country: string): number {
  return addressFormValidatorRules.countryIdValidator(country);
}

export function streetValidator(street: string): number {
  return addressFormValidatorRules.streetValidator(street);
}

export function apartmentValidator(apartment: string): number {
  return addressFormValidatorRules.apartmentValidator(apartment);
}

export function cityValidator(city: string): number {
  return addressFormValidatorRules.cityValidator(city);
}

export function provinceValidator(
  province: string,
  countryId: string,
  provinceId: string | undefined,
  countryList: Country[],
): number {
  return addressFormValidatorRules.provinceValidator(province, countryId, provinceId, countryList);
}

export function postcodeValidator(postcode: string, countryId: string): number {
  return addressFormValidatorRules.postcodeValidator(postcode, countryId);
}

export function cpfValidator(cpf: string, country: string): number {
  return addressFormValidatorRules.cpfValidator(cpf, country);
}

export function phoneValidator(
  phone: string,
  country: string,
  tel_country: string,
  tel_code: string,
): number {
  return addressFormValidatorRules.telephoneValidator(phone, country, tel_country, tel_code);
}

export const addressFormValidatorRules = {
  /**
   * 校验地址是否可邮寄
   * @param {*} val: 状态
   * @returns result
   */
  canSendValidator(val: number) {
    if (val === 2) {
      return 1;
    }
    return 0;
  },
  /**
   * 校验 firstname
   * @param {*} val: 名字
   * @returns result
   */
  firstnameValidator(val: string) {
    if (!val || !val.trim()) {
      return 2;
    }
    if (val.length < 2 || val.length > 32) {
      return 3;
    }
    if (['\\', `"`, `@`].some((item) => val.includes(item))) {
      return 4;
    }
    return 0;
  },
  /**
   * 校验 lastname
   * @param {*} val: 姓氏
   * @param {*} firstname: 名字
   * @returns result
   */
  lastnameValidator(val: string) {
    if (!val || !val.trim()) {
      return 5;
    }
    if (val.length < 2 || val.length > 32) {
      return 6;
    }
    if (['\\', `"`, `@`].some((item) => val.includes(item))) {
      return 4;
    }
    return 0;
  },
  /**
   * 联合校验 firstname & lastname 长度不能大于34
   * @param {*} firstName: 名字
   * @param {*} lastname: 姓氏
   * @returns result
   */
  firstnameAndLastnameLengthValidator(firstName: string, lastName: string) {
    if (((firstName || '') + lastName || '').length > 34) {
      return 7;
    }
    return 0;
  },
  /**
   * 校验 country_id
   * @param {*} val: 国家代码
   * @returns result
   */
  countryIdValidator(val: string) {
    if (!val) {
      return 8;
    }
    return 0;
  },
  /**
   * 校验 street
   * @param {*} val: 街道
   * @returns result
   */
  streetValidator(val: string) {
    if (!val || !val.trim()) {
      return 9;
    }
    if (val.length > 50) {
      return 10;
    }
    if (['\\', `"`, `@`].some((item) => val.includes(item))) {
      return 11;
    }
    return 0;
  },
  /**
   * 校验 city
   * @param {*} val: 城市
   * @returns result
   */
  cityValidator(val: string) {
    if (!val || !val.trim()) {
      return 12;
    }
    if (['\\', `"`, `@`].some((item) => val.includes(item))) {
      return 11;
    }
    return 0;
  },
  /**
   * 校验 province
   * @param {*} val: 州/省
   * @returns result
   */
  provinceValidator(
    val: string,
    country_id: string,
    province_id: string | number | undefined,
    countryData: Country[],
  ) {
    if (!val || !val.trim()) {
      return 13;
    }
    if (['\\', `"`, `@`].some((item) => val.includes(item))) {
      return 11;
    }

    // 当前处理只针对美国：
    // 传province不传province_id的时候，去匹配Z站地址库中的province是否存在，不存在的时候进行拦截，清空已填入的province内容（只在创建地址，更新地址，支付成功页更改地址位置校验）。
    // 前端在创建地址，更新地址，支付成功页地址编辑，payment页面老地址拦截，4处变更。
    if (country_id === 'US' && val && !province_id && `${province_id}` !== '0') {
      const usa = countryData.find((item) => item.country_id === 'US');
      const provinceList = usa?.province || [];
      const flag = provinceList?.some((item) => item.name === val);
      if (!flag) {
        return 23;
      }
    }
    return 0;
  },
  /**
   * 校验 postcode
   * @param {*} val: 邮编
   * @param {*} country_id: 国家代码
   * @returns result
   */
  postcodeValidator(val: string, country_id: string) {
    if (!val || !val.trim()) {
      return 14;
    }
    if (country_id === 'US' && !/^(?:\d{5})(?:-\d{4}|\d{4})?$/.test(val)) {
      return 15;
    }
    return 0;
  },
  isMXCPF(value: string) {
    // 格式1: 13位
    const format1 =
      /^[A-Z][AEIOU][A-Z]{2}[0-9]{2}(?:0[1-9]|1[0-2])(?:[1-2][0-9]|0[1-9]|3[0-1])[A-Z0-9]{2}[0-9A-Z]$/;

    // 格式2: 18位
    const format2 =
      /^[A-Z][AEIOU][A-Z]{2}[0-9]{2}(?:0[1-9]|1[0-2])(?:[1-2][0-9]|0[1-9]|3[0-1])[HM][A-Z]{2}[B-DF-HJ-NP-TV-Z]{3}(?:[0-9]|[A-Z])[0-9]$/;

    return format1.test(value) || format2.test(value);
  },
  /**
   * 校验 cpf
   * @param {*} val: 巴西税号，墨西哥税号
   * @param {*} country_id: 国家代码
   * @returns result
   */
  cpfValidator(val: string, country_id: string) {
    if (country_id === 'BR') {
      if (!val || !val.trim()) {
        return 16;
      }
      if (
        !(
          /^\d{3}\.\d{3}\.\d{3}-\d{2}$/.test(val) ||
          /^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/.test(val) ||
          /^\d{11}$/g.test(val) ||
          /^\d{14}$/g.test(val)
        )
      ) {
        return 17;
      }
    } else if (country_id === 'MX') {
      if (!val || !val.trim()) {
        return 24;
      }
      if (!this.isMXCPF(val)) {
        return 25;
      }
    }
    return 0;
  },
  /**
   * 校验 telephone
   * @param {*} val: 手机号
   * @param {*} val: 国家代码
   * @param {*} val: 手机归属地国家代码
   * @param {*} val: 区号
   * @returns result
   */
  telephoneValidator(
    val: string,
    country_id: string,
    tel_country: string,
    tel_code: string,
    required = true,
  ) {
    const value = val.replace(/\D/g, '');
    if (!tel_code || !tel_code.trim() || /\D/g.test(tel_code)) {
      return 18;
    }
    if (!value && required) {
      return 19;
    }
    if (value && ((country_id === 'US' && !tel_country) || tel_country === 'US')) {
      if (
        value.length !== 10 ||
        val.length > 13 ||
        (val.length === 13 && !/^\(\d{3}\)\d{3}-\d{4}$/.test(val))
      ) {
        return 20;
      }
      if (!/^[^01].*/.test(value)) {
        return 21;
      }
    }
    return 0;
  },
  /**
   * 校验 apartment
   * @param {*} val: 房间号
   * @returns result
   */
  apartmentValidator(val: string) {
    if (val.length > 20) {
      return 22;
    }
    return 0;
  },
};

// 校验地址规则
/**
 * 在支付页获取地址的校验规则，校验规则与添加地址的规则需要保持同步
 * @returns 错误码，0表示无错误
 */
export function getAddressRules(address: Address, countryList: Country[]): number {
  if (Object.keys(address).length > 0) {
    let errorCode = 0;

    errorCode = addressFormValidatorRules.canSendValidator(address.status);
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.firstnameValidator(address.firstname);
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.lastnameValidator(address.lastname);
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.firstnameAndLastnameLengthValidator(
      address.firstname,
      address.lastname,
    );
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.countryIdValidator(address.country_id);
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.streetValidator(address.street);
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.cityValidator(address.city);
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.provinceValidator(
      address.province,
      address.country_id,
      address.province_id,
      countryList,
    );
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.postcodeValidator(address.postcode, address.country_id);
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.cpfValidator(address.cpf, address.country_id);
    if (errorCode) return errorCode;

    errorCode = addressFormValidatorRules.telephoneValidator(
      address.telephone,
      address.country_id,
      address.tel_country,
      address.tel_code,
    );
    if (errorCode) return errorCode;
  }
  return 0;
}
